import { Flex, Loader, ScrollArea, Text, Title } from '@mantine/core';
import { ApiReferenceReact } from '@scalar/api-reference-react';
import '@scalar/api-reference-react/style.css';
import { useMemo } from 'react';
import { useCalcIntercept } from '../../hooks/useCalcIntercept/hook';
import { useDarkMode } from '../../hooks/useDarkMode';
import { trackEvent } from '../../util/mixpanel';
import { useOpenAPISpec } from '../../versions/use-openapi-spec';
import { versionDisplayName } from '../../versions/versions';

/**
 * Legacy versions share one openapi.json. Filter paths client-side when a calculator is selected.
 */
function LegacyScalarReference({
  version,
  calculator,
}: {
  version: string | undefined;
  calculator: string | undefined;
}) {
  const isDarkMode = useDarkMode();
  const { loading, error, selectedSpec } = useOpenAPISpec(version);
  useCalcIntercept(version);

  const filteredSpec = useMemo(() => {
    if (!selectedSpec) return null;
    if (!calculator) return selectedSpec;
    return {
      ...selectedSpec,
      paths: Object.fromEntries(
        Object.entries(selectedSpec.paths as object).filter(([path]) => path === `/${calculator}`),
      ),
    };
  }, [selectedSpec, calculator]);

  return (
    <Flex h="calc(100vh - 120px - 32px)">
      <ScrollArea scrollbars="y" flex={1} miw={0}>
        <div className="api-docs container">
          <Title order={1} mb="md">
            REST API Reference
          </Title>
          <p>
            Interactive documentation for the Carbon Calculator API. You can view the details of the parameters you need
            to supply to each calculator, and try out request directly from this page.
          </p>

          <p>
            Currently viewing version:{' '}
            <Text span fw={600}>
              {versionDisplayName(version)}
            </Text>
          </p>

          <Title order={2}>API endpoints</Title>
          <br />

          <div className="api-content">
            {loading ? (
              <Flex justify="center" align="center" w="100%" h="300px">
                <Loader size="lg" />
                <Text ml="md">Loading API specification...</Text>
              </Flex>
            ) : error ? (
              <Flex direction="column" align="center" w="100%" h="300px" justify="center">
                <Text c="red" size="lg" mb="md">
                  {error}
                </Text>
                <Text size="sm" c="dimmed">
                  Please try refreshing the page or contact support if the issue persists.
                </Text>
              </Flex>
            ) : filteredSpec ? (
              <div className="swagger-container">
                <ApiReferenceReact
                  key={calculator}
                  configuration={{
                    content: filteredSpec,
                    forceDarkModeState: isDarkMode ? 'dark' : 'light',
                    onBeforeRequest({ request }: { request: Request }) {
                      trackEvent('api_docs_request', {
                        path: request.url,
                        method: request.method,
                      });
                    },
                    hideModels: true,
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </ScrollArea>
    </Flex>
  );
}

export default LegacyScalarReference;
